/**
 * Signin API Types
 *
 * Defines request/response types for all signin flow endpoints.
 * These align with the backend API contract for authentication.
 */

// ============================================================================
// Shared Data Structures
// ============================================================================

export interface SigninUser {
  id: string;
  email: string;
  fullName: string;
  companyName?: string;
  hasMfa: boolean;
  name?: string; // Kept for backward compatibility if needed
}

// ============================================================================
// Identify Step (Optional/Legacy for SSO check)
// ============================================================================

export interface SigninIdentifyRequest {
  email: string;
  captchaToken?: string;
}

export interface SigninIdentifyResponseLocal {
  mode: "local";
}

export interface SigninIdentifyResponseSSO {
  mode: "sso";
  sso_url: string;
}

export type SigninIdentifyResponse =
  | SigninIdentifyResponseLocal
  | SigninIdentifyResponseSSO;

// ============================================================================
// Password Login Step (Expanded State Machine)
// ============================================================================

export interface SigninLoginRequest {
  email: string;
  password: string;
  captchaToken?: string;
  device_name?: string;
}

export interface SigninLoginResponseSuccess {
  status: "ok";
  user?: SigninUser;
  // Backend provided redirect URL (e.g. for session setting or external app)
  redirect_to?: string;
  authentik_session?: string;
  authentik_csrf?: string;
}

export interface SigninLoginResponseMFA {
  mfa_required: true;
  pre_mfa_token: string;
  methods: SigninMfaMethod[];
  preferred?: SigninMfaMethod;
}

// Forced Password Change State (Employee First Login)
export interface SigninLoginResponsePasswordChange {
  status: "password_change_required";
  pre_auth_token: string; // Token scoped strictly for password reset
  email: string;
}

// Forced MFA Setup State (Employee After Reset)
export interface SigninLoginResponseMfaSetup {
  status: "mfa_setup_required";
  pre_auth_token: string; // Token scoped strictly for MFA enrollment
}

export type SigninLoginResponse =
  | SigninLoginResponseSuccess
  | SigninLoginResponseMFA
  | SigninLoginResponsePasswordChange
  | SigninLoginResponseMfaSetup;

// ============================================================================
// Force Password Change (New Flow)
// ============================================================================

export interface SigninForceChangePasswordRequest {
  new_password: string;
  // pre_auth_token is sent in headers (X-Flow-Token)
}

export type SigninForceChangePasswordResponse =
  | SigninLoginResponseSuccess
  | SigninLoginResponseMfaSetup; // Can chain directly into MFA Setup

// ============================================================================
// Force MFA Setup (New Flow)
// ============================================================================

export interface SigninMfaSetupInitResponse {
  secret: string;
  qr_code: string; // Data URL
  uri?: string;
  pre_auth_token?: string; // Updated token for the confirm step (if rotated)
}

export interface SigninMfaSetupConfirmRequest {
  code: string;
  pre_auth_token: string; // Added token for authorization
}

export interface SigninMfaSetupConfirmResponse {
  status: "ok";
  user?: SigninUser;
  backup_codes?: string[];
  redirect_to?: string;
  authentik_session?: string;
  authentik_csrf?: string;
  pre_auth_token?: string; // UPDATED: Token to authorize the next step (Backup Confirm)
}

// ============================================================================
// Force Backup Codes Setup (New Flow)
// ============================================================================

export interface SigninGenerateBackupCodesResponse {
  backup_codes: string[];
  pre_auth_token?: string; // UPDATED: Token rotation support
}

export interface SigninConfirmBackupCodesResponse {
  status: "ok";
  // Added user object to populate context after final confirmation
  user?: SigninUser;
  redirect_to?: string;
  authentik_session?: string;
  authentik_csrf?: string;
}

// ============================================================================
// MFA Verification Methods
// ============================================================================

export type SigninMfaMethod = "webauthn" | "totp" | "backup_code";

export interface SigninGetMfaMethodsRequest {
  pre_mfa_token: string;
}

export interface SigninGetMfaMethodsResponse {
  methods: SigninMfaMethod[];
  preferred?: SigninMfaMethod;
}

export interface SigninVerifyTotpRequest {
  pre_mfa_token: string;
  code: string;
}

export interface SigninVerifyTotpResponse {
  status: "ok";
  // Added user object to populate context after MFA verification
  user?: SigninUser;
  redirect_to?: string;
  authentik_session?: string;
  authentik_csrf?: string;
}

export interface SigninVerifyBackupCodeRequest {
  pre_mfa_token: string;
  code: string;
}

export interface SigninVerifyBackupCodeResponse {
  status: "ok";
  // Added user object to populate context after backup code verification
  user?: SigninUser;
  redirect_to?: string;
  authentik_session?: string;
  authentik_csrf?: string;
}

// ============================================================================
// WebAuthn
// ============================================================================

export interface SigninGetWebAuthnAssertionOptionsRequest {
  pre_mfa_token: string;
}

export interface SigninWebAuthnAssertionOptionsResponse {
  challenge: string;
  timeout?: number;
  rpId?: string;
  allowCredentials?: Array<{
    type: "public-key";
    id: string;
    transports?: ("usb" | "nfc" | "ble" | "internal")[];
  }>;
  userVerification?: "required" | "preferred" | "discouraged";
}

export interface SigninVerifyWebAuthnAssertionRequest {
  pre_mfa_token: string;
  credential: {
    id: string;
    rawId: string;
    type: "public-key";
    response: {
      authenticatorData: string;
      clientDataJSON: string;
      signature: string;
      userHandle?: string;
    };
    clientExtensionResults?: Record<string, unknown>;
  };
}

export interface SigninVerifyWebAuthnAssertionResponse {
  status: "ok";
  // Added user object to populate context after WebAuthn verification
  user?: SigninUser;
  redirect_to?: string;
  authentik_session?: string;
  authentik_csrf?: string;
}

// ============================================================================
// Helper Types & Guards
// ============================================================================

export function isSigninIdentifyResponseSSO(
  response: SigninIdentifyResponse
): response is SigninIdentifyResponseSSO {
  return response.mode === "sso";
}

export function isSigninIdentifyResponseLocal(
  response: SigninIdentifyResponse
): response is SigninIdentifyResponseLocal {
  return response.mode === "local";
}

export function isSigninLoginResponseMFA(
  response: SigninLoginResponse
): response is SigninLoginResponseMFA {
  return "mfa_required" in response && response.mfa_required === true;
}

export function isSigninLoginResponseSuccess(
  response: SigninLoginResponse
): response is SigninLoginResponseSuccess {
  return "status" in response && response.status === "ok";
}

// Guards for Employee Flow
export function isSigninPasswordChangeRequired(
  response: SigninLoginResponse
): response is SigninLoginResponsePasswordChange {
  return "status" in response && response.status === "password_change_required";
}

export function isSigninMfaSetupRequired(
  response: SigninLoginResponse
): response is SigninLoginResponseMfaSetup {
  return "status" in response && response.status === "mfa_setup_required";
}
